import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import type { BrandTotals, Recipient, RecipientFilter, ReportDetail, ReportSummary, SegmentTotals } from "@/lib/types";

export const KIND_LABEL: Record<string, string> = {
  broadcast: "Broadcast",
  automation_step: "Automation email",
  transactional: "Transactional",
  keap_broadcast: "Keap broadcast (imported)",
  keap_campaign_email: "Keap campaign email (imported)",
};

/** Drill-down lists, in the order people ask for them. */
export const RECIPIENT_FILTERS: { value: RecipientFilter; label: string; count?: (s: ReportSummary) => number }[] = [
  { value: "sent", label: "Sent", count: (s) => s.sent },
  { value: "delivered", label: "Delivered", count: (s) => s.delivered },
  { value: "human_opened", label: "Opened (people)", count: (s) => s.human_opens_unique },
  { value: "opened", label: "Opened (any, incl. machines)", count: (s) => s.opens_unique },
  { value: "machine_only", label: "Machine opens only", count: (s) => s.opens_unique - s.human_opens_unique },
  { value: "not_opened", label: "Didn't open" },
  { value: "clicked", label: "Clicked", count: (s) => s.clicks_unique },
  { value: "purchased", label: "Bought", count: (s) => s.orders },
  { value: "unsubscribed", label: "Unsubscribed", count: (s) => s.unsubscribes },
  { value: "bounced", label: "Bounced", count: (s) => s.bounces_hard + s.bounces_soft },
  { value: "hard_bounced", label: "Hard bounces", count: (s) => s.bounces_hard },
  { value: "soft_bounced", label: "Soft bounces", count: (s) => s.bounces_soft },
  { value: "complained", label: "Marked as spam", count: (s) => s.complaints },
];

export interface ReportListFilters {
  brand?: number;
  kind?: string;
  since?: string;
  until?: string;
  segment?: number;
}

export function useReports(f: ReportListFilters) {
  return useInfiniteQuery({
    queryKey: ["email-reports", f],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api<{ reports: ReportSummary[] }>("/api/v1/email-reports" + qs({ brand_id: f.brand, kind: f.kind, since: f.since, until: f.until, segment_id: f.segment, limit: 100, offset: pageParam || undefined })),
    getNextPageParam: (last, all) => (last.reports.length === 100 ? all.length * 100 : undefined),
    placeholderData: (prev) => prev,
  });
}

export function useByBrand(since: string, until?: string, kind?: string) {
  return useQuery({
    queryKey: ["email-reports-by-brand", since, until, kind],
    queryFn: () => api<{ brands: BrandTotals[]; segments: SegmentTotals[] }>("/api/v1/email-reports/by-brand" + qs({ since, until, kind })),
    placeholderData: (prev) => prev,
  });
}

export function useReport(id: number) {
  return useQuery({ queryKey: ["email-report", id], queryFn: () => api<ReportDetail>(`/api/v1/email-reports/${id}`), enabled: id > 0 });
}

export function useRecipients(id: number, filter: RecipientFilter, linkKey?: string) {
  return useInfiniteQuery({
    queryKey: ["email-report-recipients", id, filter, linkKey ?? ""],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api<{ recipients: Recipient[]; next_after_contact_id?: number }>(
        `/api/v1/email-reports/${id}/recipients` + qs({ filter, link_key: linkKey, after_contact_id: pageParam || undefined, limit: 100 }),
      ),
    getNextPageParam: (last) => last.next_after_contact_id || undefined,
    enabled: id > 0,
  });
}

export function useCompare(ids: number[]) {
  return useQuery({
    queryKey: ["email-report-compare", ids.join(",")],
    queryFn: () => api<{ reports: ReportSummary[] }>("/api/v1/email-reports/compare" + qs({ ids: ids.join(",") })),
    enabled: ids.length >= 2,
  });
}

export function useTagRecipients(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { filter: RecipientFilter; link_key?: string; tag_id: number }) =>
      api<{ matched: number; tagged: number; truncated: boolean }>(`/api/v1/email-reports/${id}/recipients/tag`, { json: v }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["contacts"] }),
  });
}

export function useRebuild(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api(`/api/v1/email-reports/${id}/rebuild`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["email-report", id] });
      void qc.invalidateQueries({ queryKey: ["email-reports"] });
    },
  });
}

/** "Opened 44.1%": rates come from the API as fractions of delivered (or sent for imported history). */
export function base(s: ReportSummary): number {
  return s.delivered || s.sent;
}
